import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { BarChart, Flag, Globe } from 'lucide-react';

const SurnameHeritageChart = ({ surname_analyses = [], surnameResults = [], includeGermanicTribes = false }) => {
  // Use surnameResults if provided, otherwise surname_analyses (for backwards compatibility)
  const analyses = surnameResults.length > 0 ? surnameResults : surname_analyses;
  
  if (!analyses || analyses.length === 0) {
    return null;
  }

  // Calculate country heritage percentages
  const calculateHeritagePercentages = () => {
    const countryCount = {};
    
    // Count occurrences of each country
    analyses.forEach(analysis => {
      const country = analysis.country_origin || 'Unknown';
      countryCount[country] = (countryCount[country] || 0) + 1;
    });

    // Convert to percentages
    const total = analyses.length;
    const heritageData = Object.entries(countryCount).map(([country, count]) => ({
      country,
      count,
      percentage: Math.round((count / total) * 100)
    }));

    // Sort by percentage (highest first)
    return heritageData.sort((a, b) => b.percentage - a.percentage);
  };

  // Calculate Germanic tribe percentages
  const calculateGermanicTribes = () => {
    if (!includeGermanicTribes) return [];
    
    const tribeCount = {};
    
    // Count occurrences of each tribe
    analyses.forEach(analysis => {
      if (analysis.germanic_tribe && analysis.germanic_tribe.tribe_name) {
        const tribe = analysis.germanic_tribe.tribe_name;
        tribeCount[tribe] = (tribeCount[tribe] || 0) + 1;
      }
    });

    // Convert to percentages
    const total = analyses.length;
    const tribeData = Object.entries(tribeCount).map(([tribe, count]) => ({
      tribe,
      count,
      percentage: Math.round((count / total) * 100)
    }));

    // Sort by percentage (highest first)
    return tribeData.sort((a, b) => b.percentage - a.percentage);
  };

  const heritageData = calculateHeritagePercentages();
  const germanicTribes = calculateGermanicTribes();

  // Color palette for bars
  const getBarColor = (index) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500', 
      'bg-purple-500',
      'bg-orange-500',
      'bg-red-500',
      'bg-yellow-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
      'bg-gray-500'
    ];
    return colors[index % colors.length];
  };

  const getBarColorLight = (index) => {
    const colors = [
      'bg-blue-100',
      'bg-green-100',
      'bg-purple-100', 
      'bg-orange-100',
      'bg-red-100',
      'bg-yellow-100',
      'bg-pink-100',
      'bg-indigo-100',
      'bg-teal-100',
      'bg-gray-100'
    ];
    return colors[index % colors.length];
  };

  const getFlagEmoji = (country) => {
    const flagMap = {
      'England': '🏴󠁧󠁢󠁥󠁮󠁧󠁿',
      'Scotland': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      'Ireland': '🇮🇪',
      'Germany': '🇩🇪',
      'France': '🇫🇷',
      'Italy': '🇮🇹',
      'Spain': '🇪🇸',
      'Portugal': '🇵🇹',
      'Poland': '🇵🇱',
      'Netherlands': '🇳🇱',
      'Sweden': '🇸🇪',
      'Norway': '🇳🇴',
      'Denmark': '🇩🇰',
      'Russia': '🇷🇺',
      'Greece': '🇬🇷',
      'China': '🇨🇳',
      'Japan': '🇯🇵',
      'India': '🇮🇳',
      'Mexico': '🇲🇽',
      'Jewish Heritage': '🇮🇱',
      'United Kingdom': '🇬🇧',
      'England/Scotland': '🇬🇧',
      'Scotland/Ireland': '🇬🇧',
      'Unknown': '🏳️',
      'Requires research': '❓',
      'Requires Analysis': '🔍'
    };
    
    return flagMap[country] || '🌍';
  };

  return (
    <Card className="bg-white shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center">
          <BarChart className="w-6 h-6 mr-2 text-blue-600" />
          Family Surname Heritage Distribution
          <Badge variant="secondary" className="ml-3">
            {surname_analyses.length} Surnames Analyzed
          </Badge>
        </CardTitle>
        <p className="text-sm text-gray-600 mt-2">
          Percentage breakdown of country origins based on family surname analysis
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Chart Bars */}
          <div className="space-y-3">
            {heritageData.map((data, index) => (
              <div key={data.country} className="relative">
                {/* Country Label */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{getFlagEmoji(data.country)}</span>
                    <span className="font-medium text-gray-800">{data.country}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">
                      {data.count} surname{data.count !== 1 ? 's' : ''}
                    </Badge>
                    <span className="font-bold text-gray-900">{data.percentage}%</span>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-6 relative overflow-hidden">
                  <div 
                    className={`h-full ${getBarColor(index)} transition-all duration-1000 ease-out flex items-center justify-end pr-2`}
                    style={{ width: `${data.percentage}%` }}
                  >
                    {data.percentage > 15 && (
                      <span className="text-white text-xs font-medium">{data.percentage}%</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Germanic Tribes Distribution */}
          {germanicTribes.length > 0 && (
            <div className="mt-8 pt-6 border-t-2 border-purple-200">
              <h3 className="text-lg font-bold text-purple-900 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                </svg>
                Germanic Tribe Origins
              </h3>
              <p className="text-sm text-purple-700 mb-4">
                Ancient Germanic tribes identified in your family surnames
              </p>
              
              <div className="space-y-3">
                {germanicTribes.map((data, index) => (
                  <div key={data.tribe} className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">🛡️</span>
                        <span className="font-medium text-purple-900">{data.tribe}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="text-xs bg-purple-50 border-purple-300">
                          {data.count} surname{data.count !== 1 ? 's' : ''}
                        </Badge>
                        <span className="font-bold text-purple-900">{data.percentage}%</span>
                      </div>
                    </div>
                    
                    <div className="w-full bg-purple-100 rounded-full h-6 relative overflow-hidden">
                      <div 
                        className="h-full bg-purple-600 transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                        style={{ width: `${data.percentage}%` }}
                      >
                        {data.percentage > 15 && (
                          <span className="text-white text-xs font-medium">{data.percentage}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Statistics */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`text-center p-3 rounded-lg ${getBarColorLight(0)}`}>
                <div className="text-2xl font-bold text-gray-800">{heritageData.length}</div>
                <div className="text-sm text-gray-600">Countries Identified</div>
              </div>
              
              <div className={`text-center p-3 rounded-lg ${getBarColorLight(1)}`}>
                <div className="text-2xl font-bold text-gray-800">
                  {heritageData[0]?.percentage || 0}%
                </div>
                <div className="text-sm text-gray-600">Dominant Heritage</div>
              </div>
              
              <div className={`text-center p-3 rounded-lg ${getBarColorLight(2)}`}>
                <div className="text-2xl font-bold text-gray-800">{analyses.length}</div>
                <div className="text-sm text-gray-600">Total Surnames</div>
              </div>
            </div>
            
            {germanicTribes.length > 0 && (
              <div className="mt-4 text-center p-3 rounded-lg bg-purple-50 border border-purple-200">
                <div className="text-2xl font-bold text-purple-900">{germanicTribes.length}</div>
                <div className="text-sm text-purple-700">Germanic Tribes Identified</div>
              </div>
            )}
          </div>

          {/* Detailed Breakdown */}
          <div className="mt-4">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center">
              <Globe className="w-4 h-4 mr-2" />
              Heritage Breakdown by Surname
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {surname_analyses.map((analysis, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">{getFlagEmoji(analysis.country_origin)}</span>
                    <span className="font-medium text-sm">{analysis.surname}</span>
                  </div>
                  <span className="text-xs text-gray-600">
                    {analysis.country_origin}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Chart Footer */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 text-center">
              Heritage percentages based on surname country origins from comprehensive AI analysis. 
              Each surname contributes equally to the overall heritage calculation.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SurnameHeritageChart;